import { Injectable } from "@angular/core";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import {
  HedgeOrderDataPrimaryLookupService,
  BmarkHedgeOrdersTableDefinitionFactory,
  BmarkFixingType,
  BmarkOrderDataOperation,
  BmarkFixingData,
  ProfilePromiseActionService,
} from "shared/cubiq-services";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { GoldenLayoutService } from "app/golden-layout/golden-layout-service";
import { BfxBaseBlotterService } from "../../../../bfx-base-blotter/bfx-base-blotter.service";
import { BmarkFixingsManagerService } from "app/bfx-bmark-summary/services/bmark-fixings-manager.service";
import {
  BmarkOrderDataEvent,
  BmarkOrderDataEventPayload,
} from "shared/cubiq-services/model/benchmarks/BmarkOrderDataEvent.model";
import { BmarkOrderData } from "shared/cubiq-services/model/benchmarks/BmarkOrderData.model";
import { BmarkClientOrdersBlotterMode } from "shared/cubiq-services/model/benchmarks/enums/BmarkClientOrdersBlotterMode.enum";
import {
  RiskBMOrderDataListener,
  RiskSubscriptionType,
} from "shared/cubiq-services/lookup/model/impl/bmrisk/BaseBMRiskModel";
import { BLOTTER_RELOAD_TYPE } from "shared/cubiq-services/lookup/model/impl/bmrisk/BaseBMRiskModel";
import { filter } from "rxjs";
import {
  BmarkSummaryUtilService,
  FilterAppliedStatusModel,
} from "app/bfx-bmark-summary/services/bmark-summary-util.service";
import { BfxBlotterFilterService } from "app/bfx-blotter-filter/bfx-blotter-filter.service";
import {
  BfxBlotterFilter,
  FilterType,
  ExtendedOrderTypeIdentifier,
  BfxBlotterFilterId,
} from "app/bfx-blotter-filter/bfx-blotter-filter.constants";
import { BmarkHedgeOrdersFilterService } from "./bmark-hedge-orders-filter.service";
import { OrdersTableMode } from "shared/cubiq-services/model/OrdersTableMode";
import { OELoggerService } from "app/order-entry/services/basic/oe-logger.service";
import { BmarkSummaryViewFilterType } from "app/bfx-bmark-summary/models/bmark-summary-view-filter-type.enum";
import { BmarkRiskOrderDataProvider } from "shared/cubiq-services/lookup/model/impl/bmrisk/BaseBMRiskModel";

const ORDER_DATA_FILTERS = ["Rejected", "Cancelled", "Saved"];

/**
 * Service used to provide data to a Hedge Orders grid - there is a single global instance of this
 * service for the BM Summary view that tracks changes to the selected Fixing Data.
 *
 * There is also an instance of this service for every HOM that is opened, specific to the embedded fixing date
 * of the HOM instance.
 *
 */
@Injectable()
export class BmarkHedgeOrdersDataService
  extends BfxBaseBlotterService
  implements BmarkRiskOrderDataProvider
{
  private selectedFixingData: BmarkFixingData;
  private selectedFixingId: string;
  private selectedFixingType: BmarkFixingType = null;
  private blotterMode: BmarkClientOrdersBlotterMode = BmarkClientOrdersBlotterMode.DEALT;
  private riskListenerBlotterMode: BmarkClientOrdersBlotterMode =
    BmarkClientOrdersBlotterMode.DEALT;

  // TODO - consider dropping in BMs - no mechanism to switch & complicates service
  public mode: OrdersTableMode = OrdersTableMode.DEALT;

  public get isDealtMode() {
    return OrdersTableMode.DEALT === this.mode || OrdersTableMode.NDF_DEALT === this.mode;
  }

  private embeddedFixing: BmarkFixingData = null;
  public filterAppliedObj: FilterAppliedStatusModel = new FilterAppliedStatusModel(false, []);

  constructor(
    protected oeLogger: OELoggerService,
    public dataService: HedgeOrderDataPrimaryLookupService,
    public tableDefinitionService: BmarkHedgeOrdersTableDefinitionFactory,
    protected goldenLayoutService: GoldenLayoutService,
    private bmarkManager: BmarkFixingsManagerService,
    protected profileActionService: ProfilePromiseActionService,
    protected bmarkSummaryUtilService: BmarkSummaryUtilService,
    protected blotterFilterService: BfxBlotterFilterService,
    protected filterService: BmarkHedgeOrdersFilterService,
  ) {
    super(dataService, tableDefinitionService);
    this.oeLogger = this.oeLogger.for(__dirname);
    this.initialize();
  }

  private initialize() {
    this.dataService.isPrimed().then((primed: boolean) => {
      // Fixing Manager

      this.bmarkManager.displayFixing$.subscribe(this.onFixingManagerEvent.bind(this));

      this.dataService.bmarkManagerReady().then(() => {
        // enable queue processing - FIXME : refactor / remove
      });
      // Fixing Lookup
      this.dataService
        .getBmarkOrderDataUpdateSubject()
        .subscribe(this.onBmarkOrderDataEvent.bind(this));
    });
    this.bmarkSummaryUtilService.filterAppliedObs$
      .pipe(filter((val) => val != null))
      .subscribe(
        (filterAppliedObj: FilterAppliedStatusModel) => (this.filterAppliedObj = filterAppliedObj),
      );
  }

  /*************************************/
  /* Event handlers */

  public onFixingManagerEvent(fixingData: BmarkFixingData) {
    logger.out.info(
      `BMARK: UI-Hedge-Orders: Received BmarkFixingManagerEvent [${
        fixingData instanceof BmarkFixingData ? fixingData.id : "NULL"
      }]`,
    );
    let fixingChanged: boolean = false;
    if (fixingData instanceof BmarkFixingData) {
      if (!this.isEmbeddedBlotter()) {
        fixingChanged =
          this.selectedFixingId !== fixingData.id || this.selectedFixingType !== fixingData.type;
      } else {
        fixingChanged = this.selectedFixingData == null;
      }

      if (fixingChanged) {
        this.selectedFixingData = fixingData;
        this.selectedFixingId = fixingData.id;
        this.selectedFixingType = fixingData.type;
      }
    } else if (this.embeddedFixing == null) {
      fixingChanged = this.selectedFixingId !== "";
      this.selectedFixingData = null;
      this.selectedFixingId = "";
      this.selectedFixingType = null;
    }

    logger.out.info(
      `BMARK: UI-Hedge-Orders: Active fixing ${fixingChanged ? "CHANGED" : "SAME"} -> ${
        fixingData instanceof BmarkFixingData ? fixingData.id : "NULL"
      }`,
    );
    if (fixingChanged) {
      this.tableDataReload(BLOTTER_RELOAD_TYPE.FIXING_CHANGE);
    }
  }

  protected onBmarkOrderDataEvent($event: any): void {
    if (
      $event instanceof BmarkOrderDataEvent &&
      Array.isArray($event.operations) &&
      $event.operations.length > 0 &&
      $event.payload instanceof BmarkOrderDataEventPayload &&
      ((this.selectedFixingType == BmarkFixingType.ACTIVE &&
        typeof $event.payload.activeFixingId === "string") ||
        (this.selectedFixingType == BmarkFixingType.RECENT &&
          typeof $event.payload.recentFixingId === "string"))
    ) {
      let hasUpdate = $event.operations.includes(BmarkOrderDataOperation.UPDATE);
      let hasDelete = $event.operations.includes(BmarkOrderDataOperation.DELETE);
      let bmarkOrderDataEvent = $event as BmarkOrderDataEvent;
      let matchType =
        $event.payload.activeFixingId === this.selectedFixingId
          ? "MATCHED ACTIVE"
          : $event.payload.recentFixingId === this.selectedFixingId
            ? "MATCHED RECENT"
            : "IGNORE";
      this.outputExtendedLogging(
        `BMARK: UI-Client-Orders: Received BmarkOrderDataEvent (${matchType})-> opr: ` +
          `${$event.operations.map((item) => BmarkOrderDataOperation[item]).toString()}` +
          `${
            $event.payload.bmarkOrderData != null
              ? " new state: " + CubiqTS.OrderState[$event.payload.bmarkOrderData.orderState]
              : ""
          }  => `,
        $event,
      );

      if (
        $event.payload.activeFixingId === this.selectedFixingId ||
        $event.payload.recentFixingId === this.selectedFixingId
      ) {
        if (hasUpdate && hasDelete) {
          logger.out.error(
            `${this.constructor.name} includes both BmarkOrderDataOperation.UPDATE and BmarkOrderDataOperation.DELETE`,
          );
          logger.out.error(`${this.constructor.name} only UPDATE will be performed`);
        }

        if ($event.operations.includes(BmarkOrderDataOperation.UPDATE)) {
          if (
            BmarkFixingType.ACTIVE === this.selectedFixingData.type &&
            bmarkOrderDataEvent.payload.isFillBasedActiveToRecentMove
          ) {
            this.tableDataDeleteRow($event.payload.orderId);
          } else if (
            BmarkFixingType.ACTIVE === this.selectedFixingType ||
            BmarkFixingType.RECENT === this.selectedFixingType
          ) {
            this.tableDataUpdateRow($event.payload.bmarkOrderData);
          }
        } else if (
          $event.operations.includes(BmarkOrderDataOperation.DELETE) &&
          bmarkOrderDataEvent.payload.isFillBasedActiveToRecentMove &&
          (BmarkFixingType.RECENT === this.selectedFixingType || this.isEmbeddedBlotter())
        ) {
          this.tableDataUpdateRow($event.payload.bmarkOrderData);
        } else if ($event.operations.includes(BmarkOrderDataOperation.DELETE)) {
          this.tableDataDeleteRow($event.payload.orderId);
        }
      }
    }
  }

  /*************************************/
  /* TABLE METHODS */
  public initData(): void {
    logger.out.info(`BMARK: UI-Hedge-Orders: Table INIT`);
    this.tableDataReload(BLOTTER_RELOAD_TYPE.INIT);
  }

  private isReloadClear(reason: BLOTTER_RELOAD_TYPE): boolean {
    return (
      this.selectedFixingData == null ||
      (BLOTTER_RELOAD_TYPE.VIEW_CHANGE !== reason && BLOTTER_RELOAD_TYPE.FILTER_CHANGE !== reason)
    );
  }

  private tableDataReload(reason: BLOTTER_RELOAD_TYPE): void {
    this._rawData = Object.create(null);
    this.blotterChangeSubject.next(reason);
    logger.out.info(`BMARK: UI-Hedge-Orders: Table RELOAD`);

    if (this.isReloadClear(reason)) {
      // this.onFullUpdateEvent([], true);
    }

    if (this.selectedFixingData instanceof BmarkFixingData) {
      const requestedFixingId = this.selectedFixingData.id;

      this.dataService
        .getBmarkOrderDataForFixingId(
          this.selectedFixingData.id,
          this.isEmbeddedBlotter() ? BmarkFixingType.ALL : this.selectedFixingData.type,
        )
        .then((orderData: Array<BmarkOrderData>) => {
          if (this.selectedFixingData?.id !== requestedFixingId) {
            logger.out.info(
              `${this.constructor.name}.tableDataReload() ${BLOTTER_RELOAD_TYPE[reason]} fixing has changed during request. ignoring dataset`,
            );
            return;
          }

          if (Array.isArray(orderData)) {
            this.onFullUpdateEvent(this.processOrderData(orderData));
          }
        });
    }
  }

  protected tableDataDeleteRow(orderId: string): void {
    if (typeof orderId === "string") {
      logger.out.info(`BMARK: UI-Hedge-Orders: Table DELETE ROW: ${orderId}`);
      this.onDeleteEvent(orderId);
    } else {
      logger.out.error(`BMARK: Invalid id `, orderId);
    }
  }

  protected tableDataUpdateRow(orderData: BmarkOrderData): void {
    if (orderData instanceof CubiqTS.OrderDataImpl) {
      logger.out.info(
        `BMARK: UI-Hedge-Orders: Table UPDATE ROW: ${orderData.orderId.toString()} -> ${
          CubiqTS.OrderState[orderData.orderState]
        }`,
      );
      if (
        orderData.allocationData != null &&
        orderData.allocationData.length == 1 &&
        orderData.orderState == CubiqTS.OrderState.FILLED &&
        this.blotterMode == BmarkClientOrdersBlotterMode.RISK
      ) {
        this.getRowData()
          .filter(
            (order: CubiqTS.OrderDataImpl) =>
              order.orderId.equals(orderData.orderId) &&
              order.allocationData != null &&
              order.allocationData.length == 1 &&
              !order.allocationData[0].allocationId.equals(
                orderData.allocationData[0].allocationId,
              ),
          )
          .forEach((order: CubiqTS.OrderDataImpl) => {
            let rowId: string = order.allocationData[0].allocationId.toString();
            delete this._rawData[rowId];
            this.deleteItemSubject.next(order);
          });
      }
      this.onFullUpdateEvent(this.processOrderData(orderData));
    }
  }

  protected onDeleteEvent(rowId: string): void {
    let deletedOrder: CubiqTS.OrderData;
    if (this.blotterMode == BmarkClientOrdersBlotterMode.RISK) {
      let deletedRowIds: Array<string> = [];
      this.getRowData()
        .filter((order: CubiqTS.OrderDataImpl) => order.orderId.toString() == rowId)
        .forEach((element: CubiqTS.OrderDataImpl) => {
          if (element.allocationData != null && element.allocationData.length > 0) {
            let allocId: string = element.allocationData[0].allocationId.toString();
            deletedRowIds.push(allocId);
            deletedOrder = this._rawData[allocId];
            if (deletedOrder) {
              delete this._rawData[allocId];
            }
          }
        });
    } else {
      deletedOrder = this._rawData[rowId];
      if (deletedOrder) {
        delete this._rawData[rowId];
      }
    }

    if (deletedOrder) {
      this.deleteItemSubject.next(deletedOrder);
    }
  }

  public getItemCount(): number {
    return this.getRowData().length;
  }

  /*************************************/
  /* SPAWNED DATA */

  public setBlotterMode(tableViewType): void {
    // Hedge orders do not support RISK mode - and enabling risk mode currently
    // generates errors due to changes in allocation Ids on updates\
    if (this.riskListenerBlotterMode != tableViewType) {
      this.riskListenerBlotterMode = tableViewType;
      this.tableDataReload(BLOTTER_RELOAD_TYPE.VIEW_CHANGE);
    }
  }

  public processOrderData(
    orderData: Array<BmarkOrderData> | BmarkOrderData,
  ): Array<BmarkOrderData> {
    let result = new Array<BmarkOrderData>();

    let orderDataset = new Array<BmarkOrderData>(); // input array
    if (
      orderData instanceof CubiqTS.OrderDataImpl &&
      (BmarkFixingType.ACTIVE === this.selectedFixingType ||
        BmarkFixingType.RECENT === this.selectedFixingType)
    ) {
      orderDataset.push(orderData);
    } else if (Array.isArray(orderData)) {
      orderDataset = orderData.filter((orderData) => {
        return (
          orderData instanceof CubiqTS.OrderDataImpl &&
          (BmarkFixingType.ACTIVE === this.selectedFixingType ||
            BmarkFixingType.RECENT === this.selectedFixingType)
        );
      });
    }

    if (Array.isArray(orderDataset) && orderDataset.length > 0) {
      if (this.blotterMode === BmarkClientOrdersBlotterMode.RISK) {
        return this.generateSpawnedOrderData(orderDataset);
      } else {
        for (let orderDataItem of orderDataset) {
          if (orderDataItem != null) {
            orderDataItem.primaryKey =
              orderDataItem.orderId instanceof CubiqTS.Long
                ? orderDataItem.orderId.toString()
                : orderDataItem.orderId;

            if (orderDataItem instanceof BmarkOrderData)
              orderDataItem.blotterMode = BmarkClientOrdersBlotterMode.DEALT;

            result.push(orderDataItem);
          }
        }
      }
    }
    return result;
  }

  public generateSpawnedOrderData(orderDataset: Array<BmarkOrderData>): Array<BmarkOrderData> {
    let result = new Array<BmarkOrderData>();
    if (Array.isArray(orderDataset) && orderDataset.length > 0) {
      for (let orderDataItem of orderDataset) {
        if (
          orderDataItem instanceof CubiqTS.OrderDataImpl &&
          Array.isArray(orderDataItem.allocationData) &&
          orderDataItem.allocationData.length > 0
        ) {
          let allocationDataset = this.dataService.getAllAllocationDatasetForOrder(orderDataItem);
          for (let allocationData of allocationDataset) {
            if (
              !orderDataItem.spawned ||
              allocationData.allocationType === CubiqTS.AllocationType.COMPONENT
            ) {
              let spawnedOrderData = BmarkOrderData.clone(orderDataItem); //orderDataItem.clone();
              spawnedOrderData.allocationData = new Array(allocationData);
              spawnedOrderData.blotterMode = BmarkClientOrdersBlotterMode.RISK;
              spawnedOrderData.primaryKey =
                allocationData.allocationId instanceof CubiqTS.Long
                  ? allocationData.allocationId.toString()
                  : allocationData.allocationId;
              result.push(spawnedOrderData);
            }
          }
        }
      }
    }
    return result;
  }

  onFilterApplied() {
    this.blotterChangeSubject.next(BLOTTER_RELOAD_TYPE.FILTER_CHANGE);
  }

  // < SpotRiskBmarkOrderDataProvider
  spotRiskListeners: Set<RiskBMOrderDataListener> = new Set();

  subscribe(type: RiskSubscriptionType, listener: RiskBMOrderDataListener) {
    if (type === RiskSubscriptionType.HEDGE) {
      this.spotRiskListeners.add(listener);

      this.blotterChangeSubject.subscribe((reason: BLOTTER_RELOAD_TYPE) => {
        try {
          this.spotRiskListeners.forEach((listener) => {
            listener.onBlotterReload(reason, RiskSubscriptionType.HEDGE);
          });
        } catch (ex) {
          logger.out.error("bmhod-s - Error in BlotterChangeSubject Handler " + ex);
        }
      });

      this.deleteItemSubject.subscribe((row: any) => {
        try {
          this.spotRiskListeners.forEach((listener) => {
            let param: Set<BmarkOrderData> = new Set<BmarkOrderData>();
            param.add(row);
            listener.onRemoveOrders(
              this.processOrdersForListener(param),
              RiskSubscriptionType.HEDGE,
              false,
            );
          });
        } catch (ex) {
          logger.out.error("bmhod-s - Error in deleteItemSubject Handler " + ex);
        }
      });

      this.bmarkSummaryUtilService.filterAppliedObs$.subscribe(
        (filterAppliedObj: FilterAppliedStatusModel) => {
          if (this.selectedFixingData != null) {
            const requestedFixingId = this.selectedFixingData.id;

            this.dataService
              .getBmarkOrderDataForFixingId(
                this.selectedFixingData.id,
                this.isEmbeddedBlotter() ? BmarkFixingType.ALL : this.selectedFixingData.type,
              )
              .then((orderData: Array<BmarkOrderData>) => {
                if (this.selectedFixingData?.id !== requestedFixingId) {
                  logger.out.info(
                    `${this.constructor.name}.subscribe() FilterAppliedStatusModel fixing has changed during request. ignoring dataset`,
                  );
                  return;
                }

                try {
                  this.spotRiskListeners.forEach((listener) => {
                    if (Array.isArray(orderData)) {
                      if (
                        filterAppliedObj != null &&
                        (filterAppliedObj.isFilterApplied ||
                          filterAppliedObj.viewFilterType !== BmarkSummaryViewFilterType.ALL)
                      ) {
                        this.updateOrderDataWhenFilterApplied(
                          this.processOrderData(orderData),
                          listener,
                        );
                      } else {
                        this.updateListenerOrderData(this.processOrderData(orderData), listener);
                      }
                    }
                  });
                } catch (ex) {
                  logger.out.error(
                    "bmhod-s - Error in bmarkSummaryUtilService filterAppliedObs$ Handler " + ex,
                  );
                }
              })
              .catch((ex) =>
                logger.out.error(
                  "bmhod-s - FilterAppliedObs$ Handler: Error in getting BmarkOrderData for FixingId " +
                    ex,
                ),
              );
          }
        },
      );

      this.fullUpdateSubject.subscribe((rows: Array<any>) => {
        try {
          if (
            this.filterAppliedObj != null &&
            (this.filterAppliedObj.isFilterApplied ||
              this.filterAppliedObj.viewFilterType !== BmarkSummaryViewFilterType.ALL)
          ) {
            this.updateOrderDataWhenFilterApplied(rows, listener);
          } else {
            this.updateListenerOrderData(rows, listener);
          }
        } catch (ex) {
          logger.out.error("bmhod-s - Error in fullUpdateSubject Handler " + ex);
        }
      });
    }
  }

  /**
   * GEt the definitions of all currently applied filters - used to filter applied data when priming
   * @returns
   */
  public async getAppliedFilters(): Promise<BfxBlotterFilter[]> {
    if (this.filterAppliedObj != null && this.filterAppliedObj.isFilterApplied) {
      let filterItems: BfxBlotterFilter[] = await this.blotterFilterService.getItems(
        FilterType.BlotterFilter,
        new BfxBlotterFilterId(CubiqTS.BlotterType.ORDER, CubiqTS.AssetClass.FIX),
        ExtendedOrderTypeIdentifier.BENCHMARK,
      );

      return filterItems.filter(
        (filterItem: BfxBlotterFilter) =>
          this.filterAppliedObj.filterConfiguration.indexOf(filterItem.filterId) >= 0,
      );
    }
  }
  private async updateOrderDataWhenFilterApplied(
    rows: Array<any>,
    listener: RiskBMOrderDataListener,
  ): Promise<void> {
    let filterItems: BfxBlotterFilter[] = await this.blotterFilterService.getItems(
      FilterType.BlotterFilter,
      new BfxBlotterFilterId(CubiqTS.BlotterType.ORDER, CubiqTS.AssetClass.FIX),
      ExtendedOrderTypeIdentifier.BENCHMARK,
    );
    let selectedFilter: Array<BfxBlotterFilter> = filterItems.filter(
      (filterItem: BfxBlotterFilter) =>
        this.filterAppliedObj.filterConfiguration.indexOf(filterItem.filterId) >= 0,
    );
    let filteredOutRows: Array<any> = rows.filter((row) =>
      this.filterService.doFiltersPass(row, selectedFilter, false, this.isDealtMode),
    );
    this.updateListenerOrderData(filteredOutRows, listener);
  }

  private updateListenerOrderData(rows: Array<any>, listener: RiskBMOrderDataListener) {
    this.spotRiskListeners.forEach((listener) => {
      let param: Set<BmarkOrderData> = new Set<BmarkOrderData>();
      rows.forEach((row) => {
        if (this.bmarkSummaryUtilService.doesQuickFilterPass(row, RiskSubscriptionType.HEDGE)) {
          param.add(row);
        }
      });
      listener.onUpdateOrders(this.processOrdersForListener(param), RiskSubscriptionType.HEDGE);
    });
  }

  private processOrdersForListener(params: Set<BmarkOrderData>): Set<BmarkOrderData> {
    let result = params;

    if (params != null && this.riskListenerBlotterMode === BmarkClientOrdersBlotterMode.RISK) {
      result = new Set(this.generateSpawnedOrderData(Array.from(params)));
    }

    return result;
  }

  getOrderData(incoming: any, totalSet: any): Set<BmarkOrderData> {
    let ret: Set<BmarkOrderData> = new Set<BmarkOrderData>();
    for (const row of incoming) {
      let key = this.getRowKey(row, this.blotterMode);
      if (null != key) {
        let orderData: BmarkOrderData = totalSet[key];
        if (null != orderData) {
          ret.add(orderData); // take it from rawData as the data in the filter notif is not complete
        }
      }
    }
    return ret;
  }

  getRowKey(row: CubiqTS.OrderDataImpl, blotterMode: BmarkClientOrdersBlotterMode): string {
    let ret: string = null;
    if (this.blotterMode == BmarkClientOrdersBlotterMode.RISK) {
      if (row.allocationData != null && row.allocationData.length > 0) {
        ret = row.allocationData[0].allocationId.toString();
      }
    } else {
      ret = row.orderId.toString();
    }
    return ret;
  }

  /**
   * Get initial set of visible order hedge orders - these will be filtered by BM Summary filter
   * @returns
   */
  primeData(): Promise<Set<BmarkOrderData>> {
    let filterPromise: Promise<BfxBlotterFilter[]> = this.getAppliedFilters();

    return filterPromise.then((filters) => {
      let ret: Set<BmarkOrderData> = new Set<BmarkOrderData>();
      let innerData: Array<BmarkOrderData> = this.getRowData();

      try {
        if (filters != null && filters.length > 0) {
          innerData = innerData.filter((row) => this.filterService.doFiltersPass(row, filters));
        }
        // No need for quick search filtering as none should be defined on priming - and HOM has its own quick filter
        innerData.forEach((order) => {
          ret.add(order);
        });
      } catch (e) {
        logger.out.error(`${this.constructor.name}: error filtering out orders ${e}`);
      }
      return ret;
    });
  }

  public getEmbeddedFixing(): BmarkFixingData {
    return this.embeddedFixing;
  }

  public isEmbeddedBlotter(): boolean {
    return this.embeddedFixing != null;
  }

  public setEmbeddedBlotter(embeddedFixing: BmarkFixingData): void {
    if (embeddedFixing != null) {
      this.embeddedFixing = embeddedFixing;
    }
  }

  //  SpotRiskBmarkOrderDataProvider >
  /**********************************************************************/
  /* DEBUG - HELPER  */

  private outputExtendedLogging(value: string, dump: any = null): void {
    this.oeLogger.outputExtendedLogging(value, dump);
  }
}
